import { FlightDuration, SpendStrategy } from 'src/WebApiController';

export const MINIMUM_BASE_BID = 0.02;
export const DEFAULT_BASE_BID = 0.05;
export const DEFAULT_TARGET_ROAS = 25;

/**
 * Maps a Target ROAS value to its equivalent SpendStrategy by converting
 * the ROAS multiplier to a fee cap percentage (feeCap = 1 / targetRoas)
 * and matching it to the closest strategy tier:
 *
 *   ROAS >= 50  → feeCap ≤ 2%  → HoldForBetterPrices
 *   ROAS >= 25  → feeCap ≤ 4%  → SteadyHarvest
 *   ROAS >= 17  → feeCap ≤ 6%  → MeasuredPush
 *   ROAS >= 13  → feeCap ≤ 8%  → Accelerate
 *   ROAS <  13  → feeCap > 8%  → TurboBoost
 */
export function targetRoasToEquivalentStrategy(
  targetRoas: number
): SpendStrategy {
  if (targetRoas >= 50) return SpendStrategy.HoldForBetterPrices;
  if (targetRoas >= 25) return SpendStrategy.SteadyHarvest;
  if (targetRoas >= 17) return SpendStrategy.MeasuredPush;
  if (targetRoas >= 13) return SpendStrategy.Accelerate;
  return SpendStrategy.TurboBoost;
}

export const PERCENT_OF_UNSOLD_COST_TO_DEFAULT_BUDGET = 0.02; // 2%

export const getProspectiveBudget = (
  unsoldCst: number | undefined,
  duration: FlightDuration,
  eventStartDate?: string | null
): number | null | undefined => {
  const daysToEvent = eventStartDate
    ? Math.floor(
        (new Date(eventStartDate).getTime() - new Date().getTime()) /
          (1000 * 60 * 60 * 24)
      )
    : 0;

  if (!unsoldCst || daysToEvent <= 0) {
    return null;
  }

  const dailyBudget =
    (unsoldCst * PERCENT_OF_UNSOLD_COST_TO_DEFAULT_BUDGET) / daysToEvent; // 2% of unsold cost divided by days to event

  switch (duration) {
    case FlightDuration.Daily:
      return Math.max(Math.round(dailyBudget), 1);
    case FlightDuration.Weekly:
      return Math.max(Math.round(dailyBudget * 7), 1);
    case FlightDuration.Monthly:
      return Math.max(Math.round(dailyBudget * 30), 1);
    default:
      return null;
  }
};
